/**
 * Tint palette for a project's leading folder-glyph swatch.
 *
 * Extracted from `Sidebar.tsx` by UID-392 (it was local there since UID-367)
 * so the sidebar Projects mini-list row, the collapsed-rail letter tile, the
 * `/projects` grid card (`WorkspaceCard`) and the `/projects` list row
 * (`WorkspaceListRow`) all call the SAME function on the SAME input — the AC's
 * actual requirement is cross-surface parity: one project must show one colour
 * everywhere it appears, which two independent copies of this hash could not
 * guarantee. That also retires the "kept LOCAL here rather than extracted —
 * two call sites with two different palettes don't yet justify one" note the
 * UID-367 version carried: there are now four call sites on ONE palette.
 *
 * Comp source (code-review W3 fix): `projectDefs` (`Fictiv Platform.dc.html`
 * L7298-7300) and `createProject`'s `tints` literal (L6863/L7323) — an
 * earlier pass wrongly cited `_wsTint` (L8771) as the source; that array is
 * a DIFFERENT, unrelated 5-colour member-AVATAR palette consumed at L9950
 * (`bg:_wsTint[i%5]`), not a project tint.
 *
 * Hashed on `workspace.id`, NOT index-cycled (code-review W4 ruling): the
 * comp's tint is a PERSISTED per-project property (`p.tintFg`/`p.tintBg`
 * read straight off the project object at L8773, never off list position),
 * so a project's swatch colour never changes there. This app's
 * `GET /api/workspaces` list is sorted `updated_at desc`
 * (`apps/api/src/db/pg/tenant-workspace-repository.ts:269`), so index-
 * cycling would reshuffle EVERY row's colour whenever any one project was
 * touched — visible churn the comp doesn't have. Hashing the immutable `id`
 * (not `name` — renaming a project must not recolor it, unlike
 * `Avatar.tsx`'s name-seeded hash) reproduces the comp's observable
 * "stable, never reshuffles" guarantee with no contract/schema change; a
 * real persisted tint column would be the fully-faithful fix but is a
 * backend change, out of scope for this UI ticket.
 *
 * Array-of-class-names + `?? [0]` fallback, same shape as
 * `ShopByCategory.tsx`'s `TINT_CLASSES`/`catalog-group-tint-*` (code-review
 * W5 fix — the earlier `TINT_COUNT` + template-string form could silently
 * emit a nonexistent class name for an out-of-range index). Hash shape
 * (`hash * 31 + charCodeAt`) matches `Avatar.tsx`'s `colorFor`.
 *
 * The class names lost their UID-367 `sidebar-` prefix in UID-392 (that
 * prefix became a lie once the grid card and list row started using them);
 * the background/color rules themselves still live, unduplicated, in
 * `shell.css`. Class names are global in this app (no CSS modules), so which
 * stylesheet declares them is immaterial — but declare them ONCE. Note
 * `.sidebar-workspaces-icon` is a different, genuinely sidebar-specific
 * class (32px box sizing) and keeps its prefix.
 *
 * CONTRAST: the `color` in these classes is tuned for a DECORATIVE
 * `aria-hidden` glyph sitting beside visible project-name text (WCAG 1.4.11
 * exemption) — 3 of the 4 tints do not clear 4.5:1 as real text. Any
 * non-decorative use needs its own override, as
 * `.sidebar-workspaces-collapsed-letter` does in `shell.css`.
 */
const TINT_CLASSES = [
  'workspace-tint-0',
  'workspace-tint-1',
  'workspace-tint-2',
  'workspace-tint-3',
] as const

export function tintClassForWorkspace(id: string): string {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  }
  return TINT_CLASSES[hash % TINT_CLASSES.length] ?? TINT_CLASSES[0]
}

/**
 * UID-413 — the SAME id-hash technique as {@link tintClassForWorkspace}
 * (stable, id-seeded, never index-cycled — see that function's own
 * docstring for why), but a DIFFERENT palette: the comp's BOM-card swatch
 * uses its own `_bomTints()` array (`Fictiv Platform.dc.html:7260`), not the
 * project-swatch palette `tintClassForWorkspace` draws from. The two are
 * kept as separate functions/class lists rather than a shared parameterized
 * one — one more id-hash loop is cheap, and it keeps each palette's CSS
 * classes (declared in `workspaces.css`, scoped to `.workspace-bom-icon`)
 * next to the ONE component that uses them, rather than promoting a
 * cross-cutting "any swatch, any palette" abstraction two call sites don't
 * yet justify.
 */
const BOM_TINT_CLASSES = [
  'workspace-bom-tint-0',
  'workspace-bom-tint-1',
  'workspace-bom-tint-2',
  'workspace-bom-tint-3',
] as const

export function tintClassForBom(id: string): string {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  }
  return BOM_TINT_CLASSES[hash % BOM_TINT_CLASSES.length] ?? BOM_TINT_CLASSES[0]
}
